import { useMemo } from 'react'
import PropTypes from 'prop-types'
import Spinner from 'react-bootstrap/Spinner'
import { faCopy } from '@fortawesome/free-solid-svg-icons'
import ReviewModal from '../review/modal'
import Centered from '../review/centered'
import { formatMoney } from '../review/format'
import DuplicateCompare from './compare'
import useDuplicateReview from './use-duplicate-review'
import { analyticSimilarKeys, buildMatchMap, buildStatusMap } from './match'

const DuplicateReviewModal = ({ transaction, analytic, review, onClose, onDecided }) => {
  const { data, isLoading, isError } = useDuplicateReview({
    transactionId: transaction.id,
    analyticId: analytic.id
  })

  const sides = data?.sides || []
  const matchMap = useMemo(() => buildMatchMap(sides), [sides])
  const rule = data?.rule
  const statusMap = useMemo(
    () => buildStatusMap(sides, matchMap, analyticSimilarKeys(rule)),
    [sides, matchMap, rule]
  )
  // Server score (Monitoring::DuplicateMatchScore), the same number the card shows.
  const score = data?.score ?? null
  const scoreBasis = data?.score_basis

  const amountAtRisk = sides
    .filter((side) => side.role === 'suspect')
    .reduce((sum, side) => sum + Number(side.line_amount || 0), 0)

  const badge = score === null
    ? null
    : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--rust-100)',
            border: '1px solid var(--rust-200)'
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xl)',
              fontWeight: 700,
              color: 'var(--rust-700)',
              lineHeight: 1
            }}
          >
            {score}%
          </span>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 700,
              color: 'var(--rust-700)',
              textTransform: 'uppercase',
              letterSpacing: '.04em'
            }}
          >
            match
          </span>
        </div>
      )

  const legend = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 14,
        fontSize: 'var(--text-xs)',
        color: 'var(--text-subtle)',
        fontWeight: 600
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, background: 'var(--rust-500)' }} /> Matches
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, border: '1.5px dashed var(--gold-600)', background: 'var(--gold-100)' }} /> Similar
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--gold-500)' }} /> Differs
      </span>
    </span>
  )

  const footerMetric = amountAtRisk > 0 && (
    <div style={{ textAlign: 'right', paddingRight: 14, borderRight: '1px solid var(--border-default)' }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          color: 'var(--text-muted)'
        }}
      >
        At risk
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-lg)',
          fontWeight: 700,
          color: 'var(--rust-700)'
        }}
      >
        {formatMoney(amountAtRisk)}
      </div>
    </div>
  )

  return (
    <ReviewModal
      icon={faCopy}
      title="Duplicate review"
      subtitle={sides.length > 1
        ? `The same entity appears across ${sides.length} records — compare and decide.`
        : `Flagged by ${analytic.name}.`}
      badge={badge}
      signals={[scoreBasis, ...(data?.matched_on || [])].filter(Boolean)}
      legend={legend}
      wide={sides.length >= 3}
      analytic={analytic}
      rationale={analytic.flagged_rationale || analytic.details || 'Compare the records and decide.'}
      footerMetric={footerMetric}
      transaction={transaction}
      review={review}
      onClose={onClose}
      onDecided={onDecided}
      decidesGroup
    >
      {isLoading && (
        <Centered>
          <Spinner animation="border" size="sm" /> <span className="ms-2">Matching records…</span>
        </Centered>
      )}

      {isError && <Centered>Could not load the duplicate comparison.</Centered>}

      {!isLoading && !isError && sides.length < 2 && (
        <Centered>
          No matching record was found for this flag. The analytic may have matched on data that has
          since been archived or corrected.
        </Centered>
      )}

      {!isLoading && !isError && sides.length >= 2 && (
        <DuplicateCompare sides={sides} statusMap={statusMap} />
      )}
    </ReviewModal>
  )
}

DuplicateReviewModal.propTypes = {
  transaction: PropTypes.object.isRequired,
  analytic: PropTypes.object.isRequired,
  review: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDecided: PropTypes.func
}

export default DuplicateReviewModal
